import { Badge, Flex, Box, Text, Tooltip, Link, Image } from "@chakra-ui/react";
import { WithAuth } from "@/hoc";
import { useCharge } from "@/hooks";
import { ChargePaginator } from "@/models";
import { useEffect, useRef, useState } from "react";
import {
  formatCurrency,
  getColorBadgeStatus,
  getStatus,
  formatDate,
  showToast,
} from "@/utils";
import { useNavigate } from "react-router-dom";
import { Alert } from "@/components";
import { format } from "date-fns";
import { DynamicTable } from "@/components/DynamicTable";
import { ChargeTableFilter } from "@/components/ChargeTableFilter";
import { LocalDate } from "@js-joda/core";
import { Pagination2 } from "@/components/Pagination2";

interface FilterState {
  status: string | null
  customerName: string | null
  customerDocument: string | null
  customerEmail: string | null
  chargeTitle: string | null
  expirationDateInitial: LocalDate | null
  expirationDateFinal: LocalDate | null
  paidDateInitial: LocalDate | null
  paidDateFinal: LocalDate | null
  paymentLink: string | null
  valueMin: number | null
  valueMax: number | null
  paymentMethod: string | null
}

const Cobrancas = () => {
  const navigate = useNavigate();
  const cancelRef = useRef();
  const [data, setData] = useState({} as ChargePaginator);
  const [deleteChargeModalState, setDeleteChargeModalState] = useState(false);
  const [chargeUUID, setChargeUUID] = useState("");

  const [currentPage, setCurrentPage] = useState(0);

  const [currentFilters, setCurrentFilters] = useState<FilterState>(
    {
      status: "ALL",
      customerName: null,
      customerDocument: null,
      customerEmail: null,
      chargeTitle: null,
      expirationDateInitial: null,
      expirationDateFinal: null,
      paidDateInitial: null,
      paidDateFinal: null,
      paymentLink: null,
      valueMin: null,
      valueMax: null,
      paymentMethod: null
    }
  );
  const { getCharges, deleteCharge } = useCharge();
  const [loading, setLoading] = useState<boolean>(false);
  async function getChargesData(filterState?: FilterState) {
    setLoading(true)
    try {
      const params = {
        chargeFilterConfig: filterState?.status || "ALL",
        ...(filterState?.expirationDateInitial && {
          expirationDateInitial: filterState.expirationDateInitial.toString()
        }),
        ...(filterState?.expirationDateFinal && {
          expirationDateFinal: filterState.expirationDateFinal.toString()
        }),
        ...(filterState?.paidDateInitial && {
          paidDateInitial: filterState.paidDateInitial.toString()
        }),
        ...(filterState?.paidDateFinal && {
          paidDateFinal: filterState.paidDateFinal.toString()
        }),
        ...(filterState?.customerName && { customerName: filterState.customerName }),
        ...(filterState?.customerDocument && { customerDocument: filterState.customerDocument }),
        ...(filterState?.customerEmail && { customerEmail: filterState.customerEmail }),
        ...(filterState?.chargeTitle && { chargeTitle: filterState.chargeTitle }),
        ...(filterState?.paymentLink && { paymentLink: filterState.paymentLink }),
        ...(filterState?.valueMin && { valueMin: filterState.valueMin }),
        ...(filterState?.valueMax && { valueMax: filterState.valueMax }),
        ...(filterState?.paymentMethod && { paymentMethod: filterState.paymentMethod })
      }

      const response = await getCharges(
        params,
        {
          size: 20,
          page: currentPage,
          sort: "expirationDate,DESC",
        }
      )
      
      setData(response.data)
    } catch (error) {
      if (!filterState?.expirationDateFinal) {
        showToast({
          message: "Por Favor, selecione uma data final.",
          type: "warning",
        })
        return
      }
      
      error?.response?.data?.errors?.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      })
    } finally {
      setLoading(false)
    }
  }

  function handleToggleDeleteChargeModal() {
    setDeleteChargeModalState(!deleteChargeModalState);
  }
  
  function handleGetChargeUUID(uuid: string) {
    setChargeUUID(uuid);
    setDeleteChargeModalState(!deleteChargeModalState);
  }

  async function handleDeleteCharge(uuid: string) {
    try {
      await deleteCharge(uuid);

      getChargesData();

      showToast({
        type: "success",
        message: "Sucesso na exclusão da cobrança.",
      });

      setDeleteChargeModalState(!deleteChargeModalState);
    } catch (error) {
      error?.response?.data?.errors.map((error) => {
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        });
      });
    }
  }

  const columns = [
    {
      title: "Cliente",
      dataIndex: "customerName",
      key: "customerName",
      sorter: true,
      render: (value: string, row: any) => (
        <Flex
          flexDirection="column"
          maxWidth={["200px", "200px", "200px", "250px", "250px", "100%"]}
          fontSize="14px"
        >
          <Tooltip fontSize=".9rem" label={value}>
            <Text noOfLines={1} fontSize="14px" fontWeight="500">{value}</Text>
          </Tooltip>
          <Tooltip fontSize=".9rem" label={row.email}>
            <Text fontSize="12px">{row.email}</Text>
          </Tooltip>
        </Flex>
      )
    },
    {
      title: "Cobrança",
      dataIndex: "title",
      key: "title",
      sorter: true,
      render: (title: string) => (
        <Text w="300px" fontSize="14px" fontWeight="500">
          {title}
        </Text>
      )
    },
    {
      key: "paidDate",
      title: "Data de pagamento",
      dataIndex: "paidDate",
      sorter: true,
      render: (paidDate) =>
      (<Text w="160px" fontSize="14px">
        {formatDate(paidDate)}
      </Text>)
      ,
    },
    {
      title: "Venc.",
      dataIndex: "expirationDate",
      key: "expirationDate",
      tooltipMessage: "Vencimento",
      sorter: true,
      sortFunction: (a, b) => {
        const dateA = new Date(a.expirationDate).getTime();
        const dateB = new Date(b.expirationDate).getTime();
        return dateA - dateB;
      },
      render: (date: string) => (
        <Text fontSize="14px">
          {formatDate(date)}
        </Text>
      )
    },
    {
      key: "installments",
      title: "Parc.",
      dataIndex: "installments",
      tooltipMessage: "Parcelamento",
      sorter: true,
      render: (installments) =>
      (
      <Text w="100%" fontSize="14px" fontWeight="500">
        {installments}
      </Text>
      )
      ,
    },
    {
      title: "Valor",
      dataIndex: "value",
      key: "value",
      sorter: true,
      render: (value: number) => (
        <Text fontSize="14px">
          {formatCurrency({ value })}
        </Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: false,
      render: (status: string) => (
        <Box maxWidth="160px">
          <Badge fontSize=".9rem" variant={getColorBadgeStatus(status)}>
            {getStatus(status)}
          </Badge>
        </Box>
      )
    }, 
    {
      dataIndex: "buttonOptions",
      key: "buttonOptions",
      title: "",
      rowAction: (record) => (
        <Box 
          p="0" 
          w="300px" 
          h="30px"
        >
          <Flex
            w="300px"
            h="30px"
            gap="6px"
            align="center"
            justify="flex-end"
          >
            {record.status !== "PAID" && record.status !== "ABANDONNED"
              && record.status !== "CANCELED" && record.status !== "REFUND"
              && record.status !== "PAID_MANUALLY" && record.status !== "CHARGEBACK" && (
                <Flex
                  id="excluir_cobranca"
                  display="flex"
                  align="center"
                  justify="center"
                  h="40px"
                  w="40px"
                  bgColor="white"
                  borderRadius="8px"
                  transition="all .2s"
                  cursor="pointer"
                  _hover={{
                    transition: "all .2s",
                    bgColor: "white",
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleGetChargeUUID(record.uuid);
                  }}
                >
                  <Image
                    id="excluir_cobranca"
                    h="20px"
                    w="36px"
                    src="/images/icons/Trash.svg"
                    transition="filter .2s"
                    _hover={{
                      transition: "filter .2s",
                      filter: "brightness(.3)",
                    }}
                  />
                </Flex>
            )}
            <Flex
              id="clique_em_email"
              display="flex"
              align="center"
              justify="center"
              h="40px"
              w="40px"
              bgColor="white"
              borderRadius="8px"

              transition="all .2s"
              _hover={{
                transition: "all .2s",
                bgColor: "white",
              }}
            >
              <Link
                id="clique_em_email"
                onClick={(event) => event.stopPropagation()}
                href={`mailto:${record?.email}`}
                h="20px"
              >
                <a id="clique_em_email">
                  <Image
                    id="excluir_cobranca"
                    h="20px"
                    w="20px"
                    src="/images/icons/Mail.svg"
                    transition="filter .2s"
                    _hover={{
                      transition: "filter .2s",
                      filter: "brightness(.3)",
                    }}
                  />

                </a>
              </Link>
            </Flex>
            <Flex
              id="clique_em_whatsapp"
              display="flex"
              align="center"
              justify="center"
              h="40px"
              w="40px"
              bgColor="white"
              borderRadius="8px"
              transition="all .2s"
              _hover={{
                transition: "all .2s",
                bgColor: "white",
              }}
            >
              <Link
                id="clique_em_whatsapp"
                onClick={(event) => event.stopPropagation()}
                href={`https://Api.whatsapp.com/send?phone=55${record?.phone}`}
                target="_blank"
                h="20px"
              >
                <a id="clique_em_whatsapp">
                  <Image
                    id="clique_em_whatsapp"
                    h="20px"
                    w="20px"
                    src="/images/icons/Whatsapp.svg"
                    transition="filter .2s"
                    _hover={{
                      transition: "filter .2s",
                      filter: "brightness(.3)",
                    }}
                  />
                </a>
              </Link>
            </Flex>
            <Flex
              align="center"
              justify="center"
              h="40px"
              w="170px"
              bgColor="white"
              borderRadius="8px"
              zIndex="99"
              transition="all .2s"
              p=" 0 8px"
              _hover={{
                transition: "all .2s",
                bgColor: "white",
              }}
            >
              <Link
                onClick={(event) => event.stopPropagation()}
                href={`/cobrancas/${record?.uuid}`}
                target="_blank"
                _hover={{ cursor: "pointer" }}
                display="flex"
                alignItems="center"
                gap="10px"
              >

                <Image
                  id="excluir_cobranca"
                  h="20px"
                  w="20px"
                  src="/images/icons/Export.svg"
                  transition="filter .2s"
                  _hover={{
                    transition: "filter .2s",
                    filter: "brightness(.3)",
                  }}
                />
                <Text fontSize="12px" color="gray_600">
                  Abrir em nova aba
                </Text>
              </Link>
            </Flex>
          </Flex>
        </Box>
      ),
    },
  ];



  useEffect(() => {
    getChargesData();
  }, [currentPage]);

  const handleFilterChange = (filterState: FilterState) => {
    setCurrentFilters(filterState);
    getChargesData(filterState);
  };

  return (
    <Flex 
      flexDir="column" 
      w="100%" 
      gap="1rem"
    >
      <Flex 
        justifyContent="space-between" 
        alignItems="center"
      >
        <Text fontSize="1.5rem" fontWeight="600">
          Cobranças
        </Text>
      </Flex>
      <Box letterSpacing="tighter">
        <Alert
          closeButtonText="Não"
          confirmButtonText="Sim"
          isConfirmAlert={false}
          isOpen={deleteChargeModalState}
          onConfirm={() => handleDeleteCharge(chargeUUID)}
          onClose={handleToggleDeleteChargeModal}
          title="Tem certeza que seja deletar essa cobrança?"
          messageBody="Essa ação não poderá ser desfeita"
          leastDestructiveRef={cancelRef}
        />
        <Flex 
          flexDir="column" 
          gap="1rem"
        >
          <ChargeTableFilter 
            onFilterChange={handleFilterChange} 
            currentFilters={currentFilters}
          />
          <DynamicTable
            data={data?.content} 
            columns={columns}
            loading={loading}
            rowKey={(record) => record.uuid}
            onRow={(record) => ({
              onClick: () => navigate(`/cobrancas/${record?.uuid}`)
            })}
            locale={{ emptyText: "Sem dados de cobranças" }}
          />
          <Pagination2
            currentPage={currentPage}
            totalPages={data?.totalPages}
            onPageChange={setCurrentPage}
          />
        </Flex>
      </Box>
    </Flex>
  );
};

export default WithAuth(Cobrancas);
